/**
 * Iter 85 — Modal de gestión del catálogo "Contacto con cliente".
 *
 * Reutiliza el mismo patrón que `ModelCatalogTab.jsx` (Iter 83) pero
 * embebido en un dialog para poder gestionarse directamente desde la
 * ficha de reparación (no obliga a ir a Configuración).
 *
 * Reglas:
 *   - "WhatsApp", "Email" y "Teléfono" existen como semilla.
 *   - No hay botón eliminar — sólo desactivar.
 *   - Nombres únicos (case-insensitive) validados en cliente y servidor.
 */
import { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { GripVertical, Pencil, Plus, Save, Search, X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export default function ContactChannelManagerModal({ initialList = [], onClose, onSaved }) {
  const [list, setList] = useState(initialList);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");

  useEffect(() => { setList(initialList); setDirty(false); }, [initialList]);

  const nameExists = (name, exceptId = null) =>
    list.some((m) => m.id !== exceptId && m.name.trim().toLowerCase() === name.trim().toLowerCase());

  const addItem = () => {
    const n = newName.trim();
    if (!n) return;
    if (nameExists(n)) { toast.error("Ya existe una opción con ese nombre"); return; }
    setList([...list, { id: uuid(), name: n, active: true }]);
    setNewName("");
    setDirty(true);
  };

  const startEdit = (m) => { setEditingId(m.id); setEditingName(m.name); };

  const commitEdit = () => {
    const n = editingName.trim();
    if (!n) { toast.error("El nombre no puede quedar vacío"); return; }
    if (nameExists(n, editingId)) { toast.error("Ya existe una opción con ese nombre"); return; }
    setList(list.map((m) => (m.id === editingId ? { ...m, name: n } : m)));
    setEditingId(null); setEditingName(""); setDirty(true);
  };

  const toggleActive = (id) => {
    setList(list.map((m) => (m.id === id ? { ...m, active: !m.active } : m)));
    setDirty(true);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const filtered = filteredList;
    const movedId = filtered[from].id;
    const targetId = filtered[to].id;
    const fromFull = list.findIndex((m) => m.id === movedId);
    const toFull = list.findIndex((m) => m.id === targetId);
    const next = [...list];
    const [moved] = next.splice(fromFull, 1);
    next.splice(toFull, 0, moved);
    setList(next); setDirty(true);
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings", { contact_channel_catalog: list });
      toast.success("Opciones guardadas");
      onSaved?.(r.data.contact_channel_catalog || list);
      setDirty(false);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const filteredList = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((m) => m.name.toLowerCase().includes(term));
  }, [list, q]);

  const activeCount = list.filter((m) => m.active).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="contact-channel-modal"
    >
      <div className="w-full max-w-2xl bg-white shadow-2xl border border-[#111110] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#111110] bg-[#111110] text-white">
          <div>
            <div className="font-display font-bold text-sm">Gestionar contactos con cliente</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#A8A8A0] mt-0.5">
              {activeCount} activos · {list.length} totales
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="contact-channel-modal-close"
            className="text-white hover:text-[#DC2626] p-1"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addItem())}
              placeholder="Nombre de la opción (ej: Presencial, SMS…)"
              data-testid="contact-channel-new-input"
              className="flex-1 min-w-[200px] ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
            />
            <button
              type="button"
              onClick={addItem}
              disabled={!newName.trim()}
              data-testid="contact-channel-new-add"
              className="inline-flex items-center gap-2 bg-[#111110] hover:bg-[#6E8B3D] text-white px-3 py-2 text-sm disabled:opacity-40"
            >
              <Plus size={14} /> Añadir
            </button>
          </div>

          <div className="flex items-center gap-2 border-b border-[#E5E5E0] pb-3">
            <Search size={14} className="text-[#525252]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar opción…"
              data-testid="contact-channel-search"
              className="flex-1 bg-transparent outline-none text-sm py-1"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="text-xs text-[#525252] hover:underline">
                Limpiar
              </button>
            )}
          </div>

          {filteredList.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#525252]">
              {list.length === 0 ? "Sin opciones. Añade la primera arriba." : "Ninguna opción coincide con la búsqueda."}
            </div>
          ) : (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="channels">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="border border-[#E5E5E0]"
                    data-testid="contact-channel-list"
                  >
                    {filteredList.map((m, index) => (
                      <Draggable key={m.id} draggableId={m.id} index={index}>
                        {(prov, snapshot) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            className={`flex items-center gap-3 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 ${
                              snapshot.isDragging ? "bg-[#F0F4E8]" : m.active ? "bg-white" : "bg-[#F7F7F4]"
                            }`}
                            data-testid={`contact-channel-row-${m.id}`}
                          >
                            <span
                              {...prov.dragHandleProps}
                              className="text-[#A8A8A0] hover:text-[#111110] cursor-grab active:cursor-grabbing"
                              aria-label="Arrastrar para reordenar"
                            >
                              <GripVertical size={16} />
                            </span>
                            {editingId === m.id ? (
                              <>
                                <input
                                  value={editingName}
                                  onChange={(e) => setEditingName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
                                    if (e.key === "Escape") { setEditingId(null); setEditingName(""); }
                                  }}
                                  autoFocus
                                  data-testid={`contact-channel-edit-input-${m.id}`}
                                  className="flex-1 bg-white border border-[#6E8B3D] outline-none px-2 py-1 text-sm rounded-none"
                                />
                                <button type="button" onClick={commitEdit} className="text-[#6E8B3D] hover:text-[#556C2E]">
                                  <Check size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setEditingId(null); setEditingName(""); }}
                                  className="text-[#525252] hover:text-red-600"
                                >
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <span
                                  className={`flex-1 text-sm ${m.active ? "text-[#111110]" : "text-[#A8A8A0] line-through"}`}
                                  data-testid={`contact-channel-name-${m.id}`}
                                >
                                  {m.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => startEdit(m)}
                                  className="text-[#525252] hover:text-[#6E8B3D]"
                                  data-testid={`contact-channel-edit-${m.id}`}
                                  title="Renombrar"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleActive(m.id)}
                                  data-testid={`contact-channel-toggle-${m.id}`}
                                  className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${
                                    m.active
                                      ? "border-[#6E8B3D] text-[#6E8B3D] hover:bg-[#6E8B3D] hover:text-white"
                                      : "border-[#A8A8A0] text-[#A8A8A0] hover:bg-[#111110] hover:text-white hover:border-[#111110]"
                                  }`}
                                  title={m.active ? "Desactivar" : "Reactivar"}
                                >
                                  {m.active ? "Activo" : "Desactivado"}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          <p className="text-xs text-[#525252] leading-relaxed">
            Las opciones no se pueden eliminar — solo desactivar. Las desactivadas dejan de aparecer
            en nuevas reparaciones, pero las antiguas siguen mostrando su valor original.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#E5E5E0] bg-[#FAFAF7]">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-sm border border-[#111110] hover:bg-[#F0F0EA]"
            data-testid="contact-channel-modal-cancel"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={doSave}
            disabled={!dirty || saving}
            data-testid="contact-channel-modal-save"
            className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-sm font-semibold disabled:opacity-40"
          >
            {dirty && <span className="inline-block w-2 h-2 rounded-full bg-red-400 animate-pulse" />}
            <Save size={14} /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}
